import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/store/api';
import {
  findUserRoutingProfile,
  getTeamMemberEmail,
} from '@/utils/managerTeamMetrics';
import { getEnv } from '../../utils/env';
import { AgentStatusTable } from '@/components/common/AgentStatusTable';
import { transformMembersToStatusData } from '@/utils/agentStatusTransform';

const { useGetDirectReportsQuery, useGetUsersRoutingProfilesQuery } = api;

const {
  useGetAgentCcpStatusForUsersQuery,
  useGetAgentQueueStatusQuery,
  useGetRoutingProfilesQuery,
} = api;

/**
 * Displays real-time status of team members with queue and routing profile info
 */
export function LiveAgentStatusWidget() {
  const { t } = useTranslation();

  const {
    data: directReportsOld = [],
    isFetching: isFetchingReports,
    refetch: refetchDirectReports,
  } = useGetDirectReportsQuery();

  const useDummyData = getEnv('USE_DUMMY_DATA') === 'true';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const directReports = useDummyData
    ? [
        ...directReportsOld,

        {
          id: 'd9e931e0-51ee-4456-9bec-ad4e59d72d65',
          displayName: 'Mykyta',
          userPrincipalName: 'mbrazhynskyy@gnapartners.com',
          mail: 'mbrazhynskyy@gnapartners.com',
          jobTitle: 'Software Engineer',
        },

        {
          id: 'd70b3e95-a1a9-4bdc-a576-e15374c17cfd',
          displayName: 'Mock Parikshit',
          userPrincipalName: 'pbiradar@gnapartners.com',
          mail: 'pbiradar@gnapartners.com',
          jobTitle: 'Software Engineer',
        },
      ]
    : directReportsOld;

  const teamEmails = useMemo(
    () => directReports.map(getTeamMemberEmail).filter(Boolean) as string[],
    [directReports]
  );

  const {
    data: usersRoutingProfiles,
    isFetching: isFetchingRoutingProfiles,
    refetch: refetchUsersRoutingProfiles,
  } = useGetUsersRoutingProfilesQuery(
    { userEmails: teamEmails },
    {
      skip: teamEmails.length === 0,
      refetchOnMountOrArgChange: true,
    }
  );

  const teamConnectIds = useMemo(
    () =>
      usersRoutingProfiles?.users
        ?.map(u => u.connectId)
        ?.filter(Boolean) as string[],
    [usersRoutingProfiles]
  );

  const {
    data: agentCcpStatusData,
    isFetching: isFetchingCcpStatus,
    refetch: refetchAgentCcpStatus,
  } = useGetAgentCcpStatusForUsersQuery(
    { connectIds: teamConnectIds || [] },
    {
      skip: !teamConnectIds || teamConnectIds.length === 0,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: routingProfilesData,
    isFetching: isFetchingRoutingProfilesList,
    refetch: refetchRoutingProfiles,
  } = useGetRoutingProfilesQuery();

  const {
    data: allAgentsQueueData,
    isFetching: isFetchingQueueStatus,
    refetch: refetchQueueStatus,
  } = useGetAgentQueueStatusQuery(
    { agentIds: teamConnectIds?.join(',') || '' },
    {
      skip: !teamConnectIds || teamConnectIds.length === 0,
      refetchOnMountOrArgChange: true,
      pollingInterval: 30_000,
    }
  );

  const isRefreshing =
    isFetchingReports ||
    isFetchingRoutingProfiles ||
    isFetchingCcpStatus ||
    isFetchingQueueStatus ||
    isFetchingRoutingProfilesList;

  const isInitialLoading = isRefreshing && directReportsOld.length === 0;

  const handleRefresh = async () => {
    await Promise.all([
      refetchDirectReports(),
      refetchUsersRoutingProfiles(),
      refetchAgentCcpStatus(),
      refetchQueueStatus(),
      refetchRoutingProfiles(),
    ]);
  };

  const members = useMemo(
    () =>
      directReports.map(member => {
        const userRoutingProfile = findUserRoutingProfile(
          usersRoutingProfiles?.users,
          member
        );
        return {
          connectId: userRoutingProfile?.connectId || '',
          userEmail: getTeamMemberEmail(member) || '',
          routingProfileId: userRoutingProfile?.routingProfileId,
          routingProfileName: undefined,
        };
      }),
    [directReports, usersRoutingProfiles]
  );

  const tableMembers = useMemo(
    () =>
      transformMembersToStatusData(
        members,
        agentCcpStatusData,
        allAgentsQueueData,
        routingProfilesData,
        undefined,
        t
      ),
    [members, agentCcpStatusData, allAgentsQueueData, routingProfilesData, t]
  );

  const totalActiveCalls = useMemo(
    () => tableMembers.reduce((sum, member) => sum + member.activeCalls, 0),
    [tableMembers]
  );

  return (
    <AgentStatusTable
      members={tableMembers}
      isLoading={isInitialLoading}
      totalActiveCalls={totalActiveCalls}
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
    />
  );
}
